import {
  INPUT_JUMP,
  INPUT_LEFT,
  INPUT_RIGHT,
  INPUT_TOGGLE_CAMERA,
  INPUT_ZOOM_IN,
  INPUT_ZOOM_OUT,
  type Input,
} from '../contract.js';
import { computeScreenLayout, computeTouchControls, contains } from './layout.js';

const PREVENT_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  ' ',
  'Space',
  'KeyA',
  'KeyD',
  'KeyQ',
  'KeyE',
  'KeyC',
  'KeyB',
  'Equal',
  'Minus',
]);

export class InputBridge {
  private heldMask = 0;
  private pressedMask = 0;
  private readonly activePointers = new Map<number, number>();

  constructor(private readonly container: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    container.addEventListener('pointerdown', this.onPointerDown);
    container.addEventListener('pointerup', this.onPointerUp);
    container.addEventListener('pointercancel', this.onPointerUp);
    container.addEventListener('wheel', this.onWheel, { passive: false });
  }

  sample(): Input {
    const input = { mask: this.heldMask, pressedMask: this.pressedMask };
    this.pressedMask = 0;
    return input;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.container.removeEventListener('pointerdown', this.onPointerDown);
    this.container.removeEventListener('pointerup', this.onPointerUp);
    this.container.removeEventListener('pointercancel', this.onPointerUp);
    this.container.removeEventListener('wheel', this.onWheel);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (PREVENT_KEYS.has(event.key) || PREVENT_KEYS.has(event.code)) event.preventDefault();
    const mask = maskForKey(event);
    if (mask === 0) return;
    if ((this.heldMask & mask) === 0) this.pressedMask |= mask;
    this.heldMask |= mask;
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (PREVENT_KEYS.has(event.key) || PREVENT_KEYS.has(event.code)) event.preventDefault();
    this.heldMask &= ~maskForKey(event);
  };

  private readonly onBlur = (): void => {
    this.heldMask = 0;
    this.pressedMask = 0;
    this.activePointers.clear();
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    const bounds = this.container.getBoundingClientRect();
    const layout = computeScreenLayout(bounds.width, bounds.height);
    const controls = computeTouchControls(layout);
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    const mask = maskForTouch(controls, x, y);
    if (mask === 0) return;
    this.activePointers.set(event.pointerId, mask);
    this.heldMask |= mask;
    this.pressedMask |= mask;
    this.container.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const mask = this.activePointers.get(event.pointerId);
    if (mask === undefined) return;
    this.activePointers.delete(event.pointerId);
    this.heldMask &= ~mask;
    event.preventDefault();
  };

  private readonly onWheel = (event: WheelEvent): void => {
    this.pressedMask |= event.deltaY < 0 ? INPUT_ZOOM_IN : INPUT_ZOOM_OUT;
    event.preventDefault();
  };
}

function maskForKey(event: KeyboardEvent): number {
  switch (event.code) {
    case 'ArrowLeft':
    case 'KeyA':
      return INPUT_LEFT;
    case 'ArrowRight':
    case 'KeyD':
      return INPUT_RIGHT;
    case 'Space':
    case 'KeyB':
      return INPUT_JUMP;
    case 'KeyQ':
    case 'Minus':
      return INPUT_ZOOM_OUT;
    case 'KeyE':
    case 'Equal':
      return INPUT_ZOOM_IN;
    case 'KeyC':
      return INPUT_TOGGLE_CAMERA;
    default:
      return 0;
  }
}

function maskForTouch(controls: ReturnType<typeof computeTouchControls>, x: number, y: number): number {
  if (contains(controls.left, x, y)) return INPUT_LEFT;
  if (contains(controls.right, x, y)) return INPUT_RIGHT;
  if (contains(controls.jump, x, y)) return INPUT_JUMP;
  if (contains(controls.zoomOut, x, y)) return INPUT_ZOOM_OUT;
  if (contains(controls.zoomIn, x, y)) return INPUT_ZOOM_IN;
  return 0;
}
